import { MedicalCase } from '../../../domain/medical-case/model/medical-case';
import {
  MedicalCasePhases,
  MedicalCaseProcessingPhase,
} from '../../../domain/medical-case/model';
import { MedicalCaseState } from '../../../domain/medical-case/model/medical-case-state';
import { PipelineTab } from '../../../shared/model/pipeline-tab';
import {
  ALWAYS_DISABLED_STEPS,
  FlowPipelineDefinition,
  MedicalCasePipelineFlow,
  PREPARATION_PIPELINE_DEFINITION,
  PreparationStepKey,
  ProcessingPhasePairKey,
  StepCompletionState,
  StepProcessingState,
  WAYBACK_PIPELINE_DEFINITION,
  isFirstOpinionComplete,
} from '../model/pipeline-step.model';
import { ALL_PHASE_ORDER, VISIBLE_PHASE_ORDER } from './medical-case-document-resolver.util';

const FLOW_DEFINITIONS: Record<MedicalCasePipelineFlow, FlowPipelineDefinition> = {
  preparation: PREPARATION_PIPELINE_DEFINITION,
  wayback: WAYBACK_PIPELINE_DEFINITION,
};

export class MedicalCasePipelineStepUtil {
  static buildTabs(
    flow: MedicalCasePipelineFlow,
    stepCompletion: StepCompletionState,
    stepProcessing?: StepProcessingState,
    phases?: MedicalCasePhases | null
  ): PipelineTab[] {
    const definition = FLOW_DEFINITIONS[flow];

    return definition.tabs.map(tab => {
      const step = tab.route as PreparationStepKey;
      return {
        ...tab,
        disabled: MedicalCasePipelineStepUtil.isTabDisabled(
          flow,
          step,
          stepCompletion,
          stepProcessing
        ),
        permanentlyDisabled: ALWAYS_DISABLED_STEPS.has(step),
        processing: stepProcessing?.[step] === 'processing',
        outdated: MedicalCasePipelineStepUtil.isTabOutdated(flow, step, phases),
        error: MedicalCasePipelineStepUtil.isTabError(step, stepProcessing),
      };
    });
  }

  static deriveStepCompletion(
    flow: MedicalCasePipelineFlow,
    medicalCase: MedicalCase | null
  ): StepCompletionState {
    const definition = FLOW_DEFINITIONS[flow];
    const phases = medicalCase?.phases;
    const result: StepCompletionState = {
      upload: false,
      preview: false,
      digitization: false,
      translate: false,
      anonymize: false,
      summary: false,
      glossary: false,
      'final-report': false,
      deanonymize: false,
      package: false,
    };

    for (const step of definition.defaultUnlockedSteps ?? []) {
      result[step] = true;
    }

    if (!phases) {
      return result;
    }

    for (const { step, phase } of definition.completionPhases) {
      if (MedicalCasePipelineStepUtil.hasPhaseState(phases, phase)) {
        result[step] = true;
      }
    }

    return result;
  }

  static mapPhaseToStep(
    flow: MedicalCasePipelineFlow,
    startPhase: MedicalCaseProcessingPhase,
    endPhase: MedicalCaseProcessingPhase
  ): PreparationStepKey | null {
    const key = `${startPhase}->${endPhase}` as ProcessingPhasePairKey;
    const definition = FLOW_DEFINITIONS[flow];
    return definition.processingPairToStepMap[key] ?? null;
  }

  static mapBackendPhaseToStep(
    flow: MedicalCasePipelineFlow,
    phase: MedicalCaseProcessingPhase
  ): PreparationStepKey | null {
    const definition = FLOW_DEFINITIONS[flow];
    return definition.phaseToStepMap[phase] ?? null;
  }

  static hasFileId(
    flow: MedicalCasePipelineFlow,
    medicalCase: MedicalCase | null,
    step: PreparationStepKey
  ): boolean {
    if (!medicalCase) {
      return false;
    }

    const definition = FLOW_DEFINITIONS[flow];
    const resolver = definition.fileIdResolvers[step];
    return resolver?.(medicalCase) ?? false;
  }

  static isProcessing(medicalCase: MedicalCase | null): boolean {
    return medicalCase?.state === MedicalCaseState.DOCUMENT_PROCESSING;
  }

  static isTabDisabled(
    flow: MedicalCasePipelineFlow,
    route: PreparationStepKey,
    stepCompletion: StepCompletionState,
    stepProcessing?: StepProcessingState
  ): boolean {
    if (ALWAYS_DISABLED_STEPS.has(route)) {
      return true;
    }

    if (route === 'upload') {
      return false;
    }

    if (stepProcessing?.[route] === 'processing') {
      return false;
    }

    return !stepCompletion[route];
  }

  static isSecondOpinionUnlocked(
    phases: MedicalCasePhases | null | undefined
  ): boolean {
    return isFirstOpinionComplete(phases);
  }

  private static isTabOutdated(
    flow: MedicalCasePipelineFlow,
    step: PreparationStepKey,
    phases: MedicalCasePhases | null | undefined
  ): boolean {
    if (phases == null) {
      return false;
    }

    const definition = FLOW_DEFINITIONS[flow];
    const phase = definition.stepToPhaseMap[step];
    if (phase == null) {
      return false;
    }

    const outdatedRule = definition.outdatedRules?.[step];
    if (outdatedRule?.nullGuardPhase != null && phases[outdatedRule.nullGuardPhase]?.state == null) {
      return false;
    }

    const docsToCheck = phases[phase]?.documents ?? [];

    if (outdatedRule?.dependencyPhases) {
      const depDocs = outdatedRule.dependencyPhases.flatMap(
        depPhase => phases[depPhase]?.documents ?? []
      );
      if (depDocs.some(d => d.activeVersion?.outdated === true)) return true;
    }

    return docsToCheck.some(d => d.activeVersion?.outdated === true);
  }

  static getDocumentPhaseOrder(
    flow: MedicalCasePipelineFlow
  ): readonly MedicalCaseProcessingPhase[] {
    return FLOW_DEFINITIONS[flow].documentPhaseOrder ?? VISIBLE_PHASE_ORDER;
  }

  static getAllPhaseOrder(
    flow: MedicalCasePipelineFlow
  ): readonly MedicalCaseProcessingPhase[] {
    return FLOW_DEFINITIONS[flow].allPhaseOrder ?? ALL_PHASE_ORDER;
  }

  static getStepToPhaseMap(
    flow: MedicalCasePipelineFlow
  ): Readonly<Partial<Record<PreparationStepKey, MedicalCaseProcessingPhase>>> {
    return FLOW_DEFINITIONS[flow].stepToPhaseMap;
  }

  static getStepOrder(
    flow: MedicalCasePipelineFlow
  ): readonly PreparationStepKey[] {
    return FLOW_DEFINITIONS[flow].stepOrder;
  }

  private static isTabError(
    step: PreparationStepKey,
    stepProcessing: StepProcessingState | undefined
  ): boolean {
    return stepProcessing?.[step] === 'error';
  }

  private static hasPhaseState(
    phases: MedicalCasePhases,
    phase: MedicalCaseProcessingPhase
  ): boolean {
    return phases[phase]?.state != null;
  }

}

export const buildPreparationTabs = MedicalCasePipelineStepUtil.buildTabs;
export const derivePreparationStepCompletion =
  MedicalCasePipelineStepUtil.deriveStepCompletion;
export const mapPhaseToPreparationStep =
  MedicalCasePipelineStepUtil.mapPhaseToStep;
export const mapBackendPhaseToPreparationStep =
  MedicalCasePipelineStepUtil.mapBackendPhaseToStep;
export const hasPreparationFile = MedicalCasePipelineStepUtil.hasFileId;
export const isPreparationProcessing = MedicalCasePipelineStepUtil.isProcessing;
export const isSecondOpinionUnlocked =
  MedicalCasePipelineStepUtil.isSecondOpinionUnlocked;
